import React from 'react';

import PropTypes from 'prop-types';

import { CONTEXT_NAMES } from '@adsk/alloy-helpers';
import {
  getOrCreateGlobalContext,
  useContextWithValidation,
} from '@adsk/alloy-react-context';

import { pluginShape } from '../helpers/propTypes';
import { usePluginResolver } from '../hooks/usePluginResolver';

const ReferencePickerPluginsContext = getOrCreateGlobalContext(
  CONTEXT_NAMES.ReferencePickerPluginsContext,
);

export const useReferencePickerPlugins = () =>
  useContextWithValidation(ReferencePickerPluginsContext, {
    hook: 'useReferencePickerPlugins',
    provider: 'ReferencePickerPluginsProvider',
  });

const ReferencePickerPluginsProvider = React.forwardRef(
  ({ children, plugins: pluginsFromProp, onPluginsLoadError }, ref) => {
    const { plugins, allowedPlugins, loadingPlugins } = usePluginResolver(
      pluginsFromProp,
      onPluginsLoadError,
    );

    return (
      <ReferencePickerPluginsContext.Provider
        ref={ref}
        value={{
          plugins,
          loadingPlugins,
          allowedPlugins,
        }}
      >
        {children}
      </ReferencePickerPluginsContext.Provider>
    );
  },
);

ReferencePickerPluginsProvider.displayName = 'ReferencePickerPluginsProvider';

ReferencePickerPluginsProvider.propTypes = {
  /**
   * The entity IDs we want to get linked entities for.
   */
  plugins: PropTypes.arrayOf(
    PropTypes.oneOfType([pluginShape, PropTypes.instanceOf(Promise)]),
  ).isRequired,

  /**
   * A callback function that is called when some plugins fail to load.
   */
  onPluginsLoadError: PropTypes.func,

  /**
   * The content of the provider.
   */
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
};

export default ReferencePickerPluginsProvider;
